import { HTMLAttributes, useCallback, useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import { Icon } from '@frontend/icons';
import { useSubNavStore, type NavItem as NavItemType } from '@frontend/nav-primitives';
import { useShellTheme } from '@frontend/shell-theme';
import { Link, type AnyRoute } from '@frontend/weave-router';
import { Text, useTooltip } from '@frontend/design-system';
import { useNav } from '../providers/nav-provider';
import { notificationBubbleStyle } from '../styles';

type CustomMouseEvent<Handler = HTMLAttributes<HTMLAnchorElement>['onMouseEnter']> = Handler extends (
  e: infer E
) => void
  ? E
  : never;

const navItemStyles = css`
  text-decoration: none;
  align-items: center;
  display: flex;
  flex-direction: column;
  font-size: var(--nav-item-font-size);
  height: 3.5em;
  justify-content: center;
  position: relative;
  transition: width 0.2s ease-in-out, background 0.2s ease-in-out;
  width: var(--nav-icon-width);
`;

export const NavItem = ({ item }: { item: NavItemType }) => {
  const { iconName, label, root, subNavItems, show, disabledReason, hasCount, hasCountColor, settings } = item;
  const setCurrentParent = useNav((state) => state.setCurrentParent);
  const selectedParent = useNav((state) => state.selectedParent);
  const currentParent = useNav((state) => state.currentParent);
  const isMouseInTriangle = useNav((state) => state.isMouseInTriangle);
  const registerActiveLink = useNav((state) => state.registerActiveLink);
  const isSubMenu = useNav((state) => state.isSubMenu);
  const setOpen = useNav((state) => state.setOpen);
  const navigate = useNav((state) => state.navigate);
  const { colorObject } = useShellTheme('colorObject');

  const registerParentNav = useSubNavStore((state) => state.registerParentNav);
  const unregisterParentNav = useSubNavStore((state) => state.unregisterParentNav);
  const { Tooltip, tooltipProps, triggerProps } = useTooltip({ placement: 'right' });
  const isDisabled = !!disabledReason;

  const onMouseEnter = useCallback(() => {
    setCurrentParent(root);
  }, [root, setCurrentParent]);

  const handleClick = useCallback(() => {
    if (isDisabled) {
      return;
    }

    setCurrentParent(root);
    if (!isSubMenu) {
      setOpen(false);
    }
    if (subNavItems.length > 0) {
      setTimeout(() => {
        navigate?.(`${root}${subNavItems[0].relativePath}`);
      }, 350);
    }
  }, [isDisabled, root, setCurrentParent, isSubMenu, setOpen, subNavItems, navigate]);

  const isActive = currentParent === root;
  /**
   * ref might be null
   * So need a reliable ref to the root element
   */
  const internalRef = useRef<HTMLAnchorElement | null>(null);

  /**
   * When triggering the onMouseEnter callback from onMouseMove, make sure
   * it only fires once.
   */
  const hoverHasTriggeredRef = useRef(false);

  /**
   * If someone pauses the mouse inside the "safe" triangle for 300ms, then
   * we can assume they want to hover.
   */
  const timeoutId = useRef<number>(0);

  const onMouseMove = (e: CustomMouseEvent) => {
    if (!hoverHasTriggeredRef.current && !isMouseInTriangle({ x: e.clientX, y: e.clientY })) {
      hoverHasTriggeredRef.current = true;
      window.clearTimeout(timeoutId.current);

      /**
       * Mouse move picks up svg, span, and other child nodes of root element.
       * But internalRef is always right.
       */
      onMouseEnter?.();
    }
  };

  const handleMouseEnter = () => {
    timeoutId.current = window.setTimeout(() => {
      onMouseEnter?.();
    }, 150);

    /**
     * Always reset "has triggered" when the mouse enters, so we never get a
     * stranded link that won't trigger.
     */
    hoverHasTriggeredRef.current = false;
  };

  const handleMouseLeave = () => {
    window.clearTimeout(timeoutId.current);
  };

  useEffect(() => {
    if (!isActive) {
      return;
    }

    if (internalRef.current) {
      registerActiveLink(internalRef.current);
    }

    return () => {
      /**
       * Mouse may hover on another link, and change this link's
       * active state. So we need to clear the timeout.
       */
      window.clearTimeout(timeoutId.current);
    };
  }, [isActive, registerActiveLink]);

  useEffect(() => {
    if (isDisabled) {
      return;
    }

    registerParentNav(root, {
      label,
      root,
      subNavItems: subNavItems.map((subNavItem) => ({
        ...subNavItem,
        parentPath: root,
      })),
      settings,
    });

    return () => {
      unregisterParentNav(root);
    };
  }, [isDisabled, root, label, subNavItems, settings, registerParentNav, unregisterParentNav]);

  if (show === false) {
    return null;
  }

  const mainRoute = `${root}${subNavItems.length > 0 ? subNavItems[0].relativePath : ''}`;

  const rootStyles = [
    navItemStyles,
    css`
      color: ${colorObject.iconColor};
    `,
    notificationBubbleStyle({
      show: hasCount || false,
      backgroundColor: hasCountColor,
      borderColor:
        hasSameRootPath(selectedParent, root) || hasSameRootPath(currentParent || '', root)
          ? 'var(--colors-white)'
          : 'var(--nav-background-color)',
    }),
    hasSameRootPath(selectedParent, root) &&
      css`
        background-color: ${colorObject.hover};
      `,
    !hasSameRootPath(selectedParent, root) &&
      css`
        :hover {
          background: ${colorObject.hover};
          color: ${colorObject.activeIconColor};
        }
      `,
    isDisabled &&
      css`
        opacity: 0.55;
        cursor: not-allowed;

        :hover {
          background: transparent;
          color: ${colorObject.iconColor};
        }
      `,
  ];

  return (
    <li key={label}>
      {isDisabled ? (
        <div
          data-testid='global-nav-item'
          aria-label={label}
          aria-disabled='true'
          data-path={root}
          css={rootStyles}
          {...triggerProps}
        >
          <Icon
            name={iconName}
            size={20}
            css={{
              fontSize: 'inherit',
              color: hasSameRootPath(selectedParent, root) ? colorObject.activeIconColor : 'inherit',
              transition: 'color 0.3s ease',
            }}
          />
          <Text
            as='span'
            size='small'
            weight={hasSameRootPath(selectedParent, root) ? 'bold' : 'regular'}
            css={{
              color: hasSameRootPath(selectedParent, root) ? colorObject.activeIconColor : 'inherit',
            }}
          >
            {label}
          </Text>
        </div>
      ) : (
        <Link
          preload={'intent'}
          data-testid='global-nav-item'
          preloadIntentProximity={100}
          to={mainRoute as AnyRoute}
          aria-label={label}
          ref={internalRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onMouseMove={onMouseMove}
          data-path={root}
          onClick={handleClick}
          css={rootStyles}
        >
          <Icon
            name={iconName}
            size={20}
            css={{
              fontSize: 'inherit',
              color: hasSameRootPath(selectedParent, root) ? colorObject.activeIconColor : 'inherit',
              transition: 'color 0.3s ease',
            }}
          />
          <Text
            as='span'
            size='small'
            weight={hasSameRootPath(selectedParent, root) ? 'bold' : 'regular'}
            css={{
              color: hasSameRootPath(selectedParent, root) ? colorObject.activeIconColor : 'inherit',
            }}
          >
            {label}
          </Text>
        </Link>
      )}
      {isDisabled && disabledReason ? <Tooltip {...tooltipProps}>{disabledReason}</Tooltip> : null}
    </li>
  );
};

const hasSameRootPath = (a: string, b: string) => {
  if (!a || !b) return;
  const [aRoot, ...aRelative] = a.split('/');
  const [bRoot, ...bRelative] = b.split('/');

  return aRoot === bRoot && aRelative[0] === bRelative[0];
};
